import { useState, useEffect } from 'react';

import { Alert } from '@repo/ui/Alert';
import { Button } from '@repo/ui/Button';
import { DeleteDialog } from '@repo/ui/Dialog';
import { Card } from '@repo/ui/Card';
import { Toast } from '@repo/ui/Toast';
import { PlusIcon } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@repo/ui/Sheet';
import clsx from 'clsx';
import { TypedDocumentNode, gql } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { Mercury } from '@repo/types/Models';

// Types
interface Keyword {
  id: string;
  message: string;
  keyword: string;
  isWildcard: boolean;
  intercept: boolean;
}

// GraphQL Queries and Mutations
const GET_DID_AUTO_RESPONSE = gql`
  query GetDidAutoResponse($didId: ID!) {
    did(id: $didId) {
      account {
        id
      }
      autoResponse {
        id
        updatedAt
        name
        notes
        keywords {
          id
          message
          keyword
          isWildcard
          intercept
        }
      }
    }
  }
` as TypedDocumentNode<{
  did: {
    account?: Pick<Mercury.Models.Account, 'id'>;
    autoResponse?: {
      id: string;
      updatedAt: string;
      name: string;
      notes?: string;
      keywords: Keyword[];
    };
  };
}>;

const GET_ACCOUNT_AUTO_RESPONSE = gql`
  query GetAccountAutoResponse($accountId: ID!) {
    account(id: $accountId) {
      id
      defaultAutoResponse {
        id
        insertedAt
        updatedAt
        name
        notes
        keywords {
          id
          message
          keyword
          isWildcard
          intercept
        }
      }
    }
  }
` as TypedDocumentNode<{
  account: {
    id: string;
    defaultAutoResponse?: {
      id: string;
      insertedAt: string;
      updatedAt: string;
      name: string;
      notes?: string;
      keywords: Keyword[];
    };
  };
}>;

const CREATE_AUTO_RESPONSE_FOR_DID = gql`
  mutation CreateMessagingAutoResponseWithDid(
    $didId: ID!
    $accountId: ID!
    $input: CreateMessagingAutoResponseForAccountWithDidInput!
  ) {
    createMessagingAutoResponseForAccountWithDid(
      accountId: $accountId
      didId: $didId
      input: $input
    ) {
      messagingAutoResponse {
        id
      }
    }
  }
`;

const CREATE_AUTO_RESPONSE_FOR_ACCOUNT = gql`
  mutation CreateMessagingAutoResponseForAccount(
    $accountId: ID!
    $input: CreateMessagingAutoResponseForAccountInput!
  ) {
    createMessagingAutoResponseForAccount(accountId: $accountId, input: $input) {
      messagingAutoResponse {
        id
      }
    }
  }
`;

const UPDATE_AUTO_RESPONSE = gql`
  mutation UpdateMessagingAutoResponder($arId: ID!, $input: UpdateMessagingAutoResponseInput!) {
    updateMessagingAutoResponse(id: $arId, input: $input) {
      messagingAutoResponse {
        id
      }
    }
  }
`;

const DELETE_AUTO_RESPONSE = gql`
  mutation DeleteMessagingAutoResponse($arId: ID!, $purge: Boolean) {
    deleteMessagingAutoResponse(id: $arId, purge: $purge) {
      messagingAutoResponse {
        id
      }
    }
  }
`;

// Helper to convert keywords to API format
const keywordsToApiFormat = (keywords: Keyword[]) => {
  return keywords.map((kw) => ({
    id: kw.id,
    message: kw.message,
    keyword: kw.isWildcard ? 'WILDCARD' : kw.keyword,
    isWildcard: kw.isWildcard,
    intercept: kw.intercept,
  }));
};

// ============================================================================
// CreateKeywordSheet - Self-contained form for creating a new keyword
// ============================================================================
interface CreateKeywordSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingKeywords: Keyword[];
  autoResponseId?: string;
  accountId?: string;
  didId?: string;
  onSuccess: () => Promise<void>;
  onSuccessToast: (message: string) => void;
  onErrorToast: (message: string) => void;
}

const CreateKeywordSheet = ({
  open,
  onOpenChange,
  existingKeywords,
  autoResponseId,
  accountId,
  didId,
  onSuccess,
  onSuccessToast,
  onErrorToast,
}: CreateKeywordSheetProps) => {
  const client = useApolloClient();
  const isDid = !!didId;

  const [keyword, setKeyword] = useState('');
  const [message, setMessage] = useState('');
  const [forward, setForward] = useState(true);
  const [errors, setErrors] = useState<{ keyword?: string; message?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setKeyword('');
    setMessage('');
    setForward(true);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSave = async () => {
    const newErrors: { keyword?: string; message?: string } = {};

    if (keyword.length < 2) {
      newErrors.keyword = 'Keyword must contain at least 2 characters.';
    }
    if (!message) {
      newErrors.message = 'A message is required.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const newKeyword = {
        keyword,
        message,
        isWildcard: false,
        intercept: !forward,
      };

      const keywordsInput = [...keywordsToApiFormat(existingKeywords), newKeyword];

      if (!autoResponseId) {
        // Create new auto response
        if (isDid) {
          await client.mutate({
            mutation: CREATE_AUTO_RESPONSE_FOR_DID,
            variables: {
              didId,
              accountId,
              input: {
                name: `Auto Generated for DID: ${didId}`,
                keywords: keywordsInput,
              },
            },
          });
        } else {
          await client.mutate({
            mutation: CREATE_AUTO_RESPONSE_FOR_ACCOUNT,
            variables: {
              accountId,
              input: {
                name: `Auto Generated for Account: ${accountId}`,
                keywords: keywordsInput,
              },
            },
          });
        }
      } else {
        // Update existing
        await client.mutate({
          mutation: UPDATE_AUTO_RESPONSE,
          variables: {
            arId: autoResponseId,
            input: { keywords: keywordsInput },
          },
        });
      }

      await onSuccess();
      resetForm();
      onOpenChange(false);
      onSuccessToast('Keyword created successfully');
    } catch (error) {
      console.error('Failed to create keyword:', error);
      onErrorToast('Failed to create keyword');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>New Keyword</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-6 p-4">
          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Keyword</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                if (errors.keyword) setErrors((prev) => ({ ...prev, keyword: undefined }));
              }}
              className={clsx(
                'relative w-full p-2 border rounded-md bg-grey-100 border-grey-500 placeholder:text-grey-500 text-grey-1000 dark:text-blue-100 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none',
                { 'border-red-500 focus:border-red-300': !!errors.keyword }
              )}
            />
            {errors.keyword && <span className="text-sm text-red-500">{errors.keyword}</span>}
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Forward</label>
            <button
              type="button"
              role="switch"
              aria-checked={forward}
              onClick={() => setForward(!forward)}
              className={clsx(
                forward ? 'bg-blue-700 dark:bg-blue-500' : 'bg-gray-400',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
              )}
            >
              <span className="sr-only">Forward</span>
              <span
                aria-hidden="true"
                className={clsx(
                  forward ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                )}
              />
            </button>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Message</label>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
              }}
              rows={4}
              className={clsx(
                'relative w-full p-2 border rounded-md bg-grey-100 border-grey-500 placeholder:text-grey-500 text-grey-1000 dark:text-blue-100 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none resize-none',
                { 'border-red-500 focus:border-red-300': !!errors.message }
              )}
            />
            {errors.message && <span className="text-sm text-red-500">{errors.message}</span>}
          </div>
        </div>
        <SheetFooter>
          <Button type="button" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// CreateWildcardSheet - Self-contained form for creating a general auto responder
// ============================================================================
interface CreateWildcardSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingKeywords: Keyword[];
  autoResponseId?: string;
  accountId?: string;
  didId?: string;
  onSuccess: () => Promise<void>;
  onSuccessToast: (message: string) => void;
  onErrorToast: (message: string) => void;
}

const CreateWildcardSheet = ({
  open,
  onOpenChange,
  existingKeywords,
  autoResponseId,
  accountId,
  didId,
  onSuccess,
  onSuccessToast,
  onErrorToast,
}: CreateWildcardSheetProps) => {
  const client = useApolloClient();
  const isDid = !!didId;

  const [message, setMessage] = useState('');
  const [forward, setForward] = useState(true);
  const [errors, setErrors] = useState<{ message?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setMessage('');
    setForward(true);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSave = async () => {
    const newErrors: { message?: string } = {};

    if (!message) {
      newErrors.message = 'A message is required.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const newWildcard = {
        keyword: 'WILDCARD',
        message,
        isWildcard: true,
        intercept: !forward,
      };

      const keywordsInput = [...keywordsToApiFormat(existingKeywords), newWildcard];

      if (!autoResponseId) {
        // Create new auto response
        if (isDid) {
          await client.mutate({
            mutation: CREATE_AUTO_RESPONSE_FOR_DID,
            variables: {
              didId,
              accountId,
              input: {
                name: `Auto Generated for DID: ${didId}`,
                keywords: keywordsInput,
              },
            },
          });
        } else {
          await client.mutate({
            mutation: CREATE_AUTO_RESPONSE_FOR_ACCOUNT,
            variables: {
              accountId,
              input: {
                name: `Auto Generated for Account: ${accountId}`,
                keywords: keywordsInput,
              },
            },
          });
        }
      } else {
        // Update existing
        await client.mutate({
          mutation: UPDATE_AUTO_RESPONSE,
          variables: {
            arId: autoResponseId,
            input: { keywords: keywordsInput },
          },
        });
      }

      await onSuccess();
      resetForm();
      onOpenChange(false);
      onSuccessToast('General auto responder created successfully');
    } catch (error) {
      console.error('Failed to create general auto responder:', error);
      onErrorToast('Failed to create general auto responder');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>New General Auto Responder</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-6 p-4">
          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Forward</label>
            <button
              type="button"
              role="switch"
              aria-checked={forward}
              onClick={() => setForward(!forward)}
              className={clsx(
                forward ? 'bg-blue-700 dark:bg-blue-500' : 'bg-gray-400',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
              )}
            >
              <span className="sr-only">Forward</span>
              <span
                aria-hidden="true"
                className={clsx(
                  forward ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                )}
              />
            </button>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Message</label>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
              }}
              rows={4}
              className={clsx(
                'relative w-full p-2 border rounded-md bg-grey-100 border-grey-500 placeholder:text-grey-500 text-grey-1000 dark:text-blue-100 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none resize-none',
                { 'border-red-500 focus:border-red-300': !!errors.message }
              )}
            />
            {errors.message && <span className="text-sm text-red-500">{errors.message}</span>}
          </div>
        </div>
        <SheetFooter>
          <Button type="button" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// EditKeywordSheet - Self-contained form for editing a keyword
// ============================================================================
interface EditKeywordSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keywordData: Keyword;
  allKeywords: Keyword[];
  autoResponseId: string;
  canUpdate?: boolean;
  onSuccess: () => Promise<void>;
  onSuccessToast: (message: string) => void;
  onErrorToast: (message: string) => void;
}

const EditKeywordSheet = ({
  open,
  onOpenChange,
  keywordData,
  allKeywords,
  autoResponseId,
  canUpdate,
  onSuccess,
  onSuccessToast,
  onErrorToast,
}: EditKeywordSheetProps) => {
  const client = useApolloClient();

  const [keyword, setKeyword] = useState(keywordData.keyword);
  const [message, setMessage] = useState(keywordData.message);
  const [forward, setForward] = useState(!keywordData.intercept);
  const [errors, setErrors] = useState<{ keyword?: string; message?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    const newErrors: { keyword?: string; message?: string } = {};

    if (keyword.length < 2) {
      newErrors.keyword = 'Keyword must contain at least 2 characters.';
    }
    if (!message) {
      newErrors.message = 'A message is required.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const keywordsInput = allKeywords.map((kw) =>
        kw.id === keywordData.id
          ? {
              id: kw.id,
              keyword,
              message,
              isWildcard: false,
              intercept: !forward,
            }
          : {
              id: kw.id,
              keyword: kw.isWildcard ? 'WILDCARD' : kw.keyword,
              message: kw.message,
              isWildcard: kw.isWildcard,
              intercept: kw.intercept,
            }
      );

      await client.mutate({
        mutation: UPDATE_AUTO_RESPONSE,
        variables: {
          arId: autoResponseId,
          input: { keywords: keywordsInput },
        },
      });

      await onSuccess();
      onOpenChange(false);
      onSuccessToast('Keyword updated successfully');
    } catch (error) {
      console.error('Failed to update keyword:', error);
      onErrorToast('Failed to update keyword');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{keywordData.keyword || 'Edit Keyword'}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-6 p-4">
          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Keyword</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                if (errors.keyword) setErrors((prev) => ({ ...prev, keyword: undefined }));
              }}
              disabled={!canUpdate}
              className={clsx(
                'relative w-full p-2 border rounded-md bg-grey-100 border-grey-500 placeholder:text-grey-500 text-grey-1000 dark:text-blue-100 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none',
                { 'border-red-500 focus:border-red-300': !!errors.keyword },
                { 'opacity-50 cursor-not-allowed': !canUpdate }
              )}
            />
            {errors.keyword && <span className="text-sm text-red-500">{errors.keyword}</span>}
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Forward</label>
            <button
              type="button"
              role="switch"
              aria-checked={forward}
              onClick={() => canUpdate && setForward(!forward)}
              disabled={!canUpdate}
              className={clsx(
                forward ? 'bg-blue-700 dark:bg-blue-500' : 'bg-gray-400',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2',
                { 'opacity-50 cursor-not-allowed': !canUpdate }
              )}
            >
              <span className="sr-only">Forward</span>
              <span
                aria-hidden="true"
                className={clsx(
                  forward ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                )}
              />
            </button>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Message</label>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
              }}
              rows={4}
              disabled={!canUpdate}
              className={clsx(
                'relative w-full p-2 border rounded-md bg-grey-100 border-grey-500 placeholder:text-grey-500 text-grey-1000 dark:text-blue-100 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none resize-none',
                { 'border-red-500 focus:border-red-300': !!errors.message },
                { 'opacity-50 cursor-not-allowed': !canUpdate }
              )}
            />
            {errors.message && <span className="text-sm text-red-500">{errors.message}</span>}
          </div>
        </div>
        {canUpdate && (
          <SheetFooter>
            <Button type="button" onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// EditWildcardSheet - Self-contained form for editing a general auto responder
// ============================================================================
interface EditWildcardSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wildcardData: Keyword;
  allKeywords: Keyword[];
  autoResponseId: string;
  canUpdate?: boolean;
  onSuccess: () => Promise<void>;
  onSuccessToast: (message: string) => void;
  onErrorToast: (message: string) => void;
}

const EditWildcardSheet = ({
  open,
  onOpenChange,
  wildcardData,
  allKeywords,
  autoResponseId,
  canUpdate,
  onSuccess,
  onSuccessToast,
  onErrorToast,
}: EditWildcardSheetProps) => {
  const client = useApolloClient();

  const [message, setMessage] = useState(wildcardData.message);
  const [forward, setForward] = useState(!wildcardData.intercept);
  const [errors, setErrors] = useState<{ message?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    const newErrors: { message?: string } = {};

    if (!message) {
      newErrors.message = 'A message is required.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const keywordsInput = allKeywords.map((kw) =>
        kw.id === wildcardData.id
          ? {
              id: kw.id,
              keyword: 'WILDCARD',
              message,
              isWildcard: true,
              intercept: !forward,
            }
          : {
              id: kw.id,
              keyword: kw.isWildcard ? 'WILDCARD' : kw.keyword,
              message: kw.message,
              isWildcard: kw.isWildcard,
              intercept: kw.intercept,
            }
      );

      await client.mutate({
        mutation: UPDATE_AUTO_RESPONSE,
        variables: {
          arId: autoResponseId,
          input: { keywords: keywordsInput },
        },
      });

      await onSuccess();
      onOpenChange(false);
      onSuccessToast('General auto responder updated successfully');
    } catch (error) {
      console.error('Failed to update general auto responder:', error);
      onErrorToast('Failed to update general auto responder');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>General Auto Responder</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-6 p-4">
          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Forward</label>
            <button
              type="button"
              role="switch"
              aria-checked={forward}
              onClick={() => canUpdate && setForward(!forward)}
              disabled={!canUpdate}
              className={clsx(
                forward ? 'bg-blue-700 dark:bg-blue-500' : 'bg-gray-400',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2',
                { 'opacity-50 cursor-not-allowed': !canUpdate }
              )}
            >
              <span className="sr-only">Forward</span>
              <span
                aria-hidden="true"
                className={clsx(
                  forward ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                )}
              />
            </button>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium text-grey-700 dark:text-grey-300">Message</label>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
              }}
              rows={4}
              disabled={!canUpdate}
              className={clsx(
                'relative w-full p-2 border rounded-md bg-grey-100 border-grey-500 placeholder:text-grey-500 text-grey-1000 dark:text-blue-100 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none resize-none',
                { 'border-red-500 focus:border-red-300': !!errors.message },
                { 'opacity-50 cursor-not-allowed': !canUpdate }
              )}
            />
            {errors.message && <span className="text-sm text-red-500">{errors.message}</span>}
          </div>
        </div>
        {canUpdate && (
          <SheetFooter>
            <Button type="button" onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// Main Component
// ============================================================================
interface AutoResponderCrudFormProps {
  didId?: string;
  accountId?: string;
  canUpdate?: boolean;
  canDelete?: boolean;
}

const AutoResponderCrudForm = ({
  didId,
  accountId,
  canUpdate,
  canDelete,
}: AutoResponderCrudFormProps) => {
  const client = useApolloClient();
  const isDid = !!didId;

  // Query data
  const {
    data: didData,
    loading: didLoading,
    refetch: refetchDid,
  } = useQuery(GET_DID_AUTO_RESPONSE, {
    variables: { didId: didId! },
    skip: !isDid,
  });

  const {
    data: accountData,
    loading: accountLoading,
    refetch: refetchAccount,
  } = useQuery(GET_ACCOUNT_AUTO_RESPONSE, {
    variables: { accountId: accountId! },
    skip: isDid || !accountId,
  });

  // Extract current data
  const loading = isDid ? didLoading : accountLoading;
  const autoResponse = isDid
    ? didData?.did?.autoResponse
    : accountData?.account?.defaultAutoResponse;
  const currentAccountId = isDid ? didData?.did?.account?.id : accountData?.account?.id;
  const keywords = autoResponse?.keywords ?? [];

  // Find wildcard and keyword items
  const wildcardItem = keywords.find((kw) => kw.isWildcard);
  const keywordItems = keywords.filter((kw) => !kw.isWildcard);

  // Refetch helper
  const refetch = async () => {
    if (isDid) {
      await refetchDid();
    } else {
      await refetchAccount();
    }
  };

  // State for sheets/dialogs
  const [createKeywordOpen, setCreateKeywordOpen] = useState(false);
  const [createWildcardOpen, setCreateWildcardOpen] = useState(false);
  const [editKeywordId, setEditKeywordId] = useState<string | null>(null);
  const [editWildcardOpen, setEditWildcardOpen] = useState(false);
  const [deleteKeywordId, setDeleteKeywordId] = useState<string | null>(null);
  const [deleteWildcardOpen, setDeleteWildcardOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showSuccessToast = (message: string) => setToast({ type: 'success', message });
  const showErrorToast = (message: string) => setToast({ type: 'error', message });

  // Find keyword to edit
  const keywordToEdit = editKeywordId ? keywords.find((kw) => kw.id === editKeywordId) : null;
  const keywordToDelete = deleteKeywordId ? keywords.find((kw) => kw.id === deleteKeywordId) : null;

  // Delete handlers (not forms, just mutation calls)
  const handleDeleteKeyword = async () => {
    if (!deleteKeywordId || !autoResponse?.id) return;

    setIsDeleting(true);
    try {
      const remainingKeywords = keywords.filter((kw) => kw.id !== deleteKeywordId);

      if (remainingKeywords.length === 0) {
        await client.mutate({
          mutation: DELETE_AUTO_RESPONSE,
          variables: { arId: autoResponse.id, purge: isDid },
        });
      } else {
        await client.mutate({
          mutation: UPDATE_AUTO_RESPONSE,
          variables: {
            arId: autoResponse.id,
            input: { keywords: keywordsToApiFormat(remainingKeywords) },
          },
        });
      }
      await refetch();
      setDeleteKeywordId(null);
      showSuccessToast('Keyword deleted successfully');
    } catch (error) {
      console.error('Failed to delete keyword:', error);
      showErrorToast('Failed to delete keyword');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteWildcard = async () => {
    if (!wildcardItem || !autoResponse?.id) return;

    setIsDeleting(true);
    try {
      const remainingKeywords = keywords.filter((kw) => !kw.isWildcard);

      if (remainingKeywords.length === 0) {
        await client.mutate({
          mutation: DELETE_AUTO_RESPONSE,
          variables: { arId: autoResponse.id, purge: isDid },
        });
      } else {
        await client.mutate({
          mutation: UPDATE_AUTO_RESPONSE,
          variables: {
            arId: autoResponse.id,
            input: { keywords: keywordsToApiFormat(remainingKeywords) },
          },
        });
      }
      await refetch();
      setDeleteWildcardOpen(false);
      showSuccessToast('General auto responder deleted successfully');
    } catch (error) {
      console.error('Failed to delete general auto responder:', error);
      showErrorToast('Failed to delete general auto responder');
    } finally {
      setIsDeleting(false);
    }
  };

  // Only show the skeleton on the very first load. Without this guard, every
  // `refetch()` (delete/edit/save) flips `loading` true and unmounts the whole
  // card, causing a visible flicker before the success toast fires.
  if (loading && !autoResponse) {
    return (
      <Card skeletonCount={7} title="Auto Responders" description="Loading..." className="w-full" />
    );
  }

  return (
    <Card
      title="Auto Responders"
      description="Configure automatic responses for incoming messages. The general auto responder replies to all messages, while keywords respond to specific trigger words."
      className="w-full max-w-xl"
    >
      {/* General Auto Responder Section */}
      <div className="flex flex-col md:col-span-2">
        <div className="my-6 w-full bg-grey-400 dark:bg-grey-800 h-[1px]"></div>
        <h3 className="mb-4 text-sm font-semibold uppercase text-grey-600">
          General Auto Responder
        </h3>

        {wildcardItem ? (
          <div className="flex flex-col gap-4 max-w-xl">
            <Alert variant="info">
              {!wildcardItem.intercept ? 'Forwarding enabled' : 'Forwarding disabled'}
            </Alert>
            <div className="flex flex-col gap-2 w-full">
              <label className="text-sm font-medium text-grey-700 dark:text-grey-300">
                Message
              </label>
              <textarea
                disabled
                rows={3}
                value={wildcardItem.message}
                onChange={() => {}}
                className="relative p-2 w-full rounded-md border opacity-50 cursor-not-allowed resize-none bg-grey-100 border-grey-500 text-grey-1000 dark:text-blue-100 dark:bg-blue-900 dark:border-grey-600 focus:outline-none"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" type="button" onClick={() => setEditWildcardOpen(true)}>
                {canUpdate ? 'Edit' : 'View'}
              </Button>
              {canDelete && (
                <Button
                  variant="danger-secondary"
                  type="button"
                  onClick={() => setDeleteWildcardOpen(true)}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-xl">
            {canUpdate ? (
              <Button variant="secondary" type="button" onClick={() => setCreateWildcardOpen(true)}>
                <PlusIcon className="mr-2 w-4 h-4" />
                Create general auto responder
              </Button>
            ) : (
              <div className="bg-grey-100 px-5 py-2.5 rounded-xl border border-grey-1000">
                <span className="text-grey-600">No general auto responder configured</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Keywords Section */}
      <div className="flex flex-col w-full md:col-span-2">
        <div className="my-6 w-full bg-grey-400 dark:bg-grey-800 h-[1px]"></div>
        <h3 className="mb-4 text-sm font-semibold uppercase text-grey-600">Keywords</h3>

        {keywordItems.length > 0 ? (
          <div className="overflow-hidden relative mb-6 max-w-xl rounded-xl border border-grey-1000 dark:border-grey-600">
            <div className="bg-grey-100 dark:bg-blue-900 px-5 py-2.5 rounded-t-xl border-b border-grey-1000 dark:border-grey-600">
              <span className="uppercase text-grey-600 dark:text-blue-100">keyword</span>
            </div>
            <ul className="w-full">
              {keywordItems.map((kw) => (
                <li key={kw.id} className="border-b last-of-type:border-b-0 border-b-grey-1000 dark:border-b-grey-600">
                  <div className="flex gap-2.5 py-2 px-3 min-w-0">
                    <div className="flex justify-between items-center gap-2 w-full min-w-0 text-left">
                      <span className="font-bold min-w-0 flex-1 truncate" title={kw.keyword}>
                        {kw.keyword}{' '}
                        <span className="font-normal text-grey-600">
                          ({kw.intercept ? 'No Forward' : 'Forward'})
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditKeywordId(kw.id)}
                        className="block shrink-0 px-4 py-1 h-full text-sm font-bold text-blue-700 bg-transparent rounded-md border-2 border-blue-700 hover:bg-blue-200 dark:text-blue-500"
                      >
                        {canUpdate ? 'Update' : 'View'}
                      </button>
                    </div>
                    {canDelete && (
                      <Button
                        variant="danger-secondary"
                        size="sm"
                        className="shrink-0"
                        onClick={() => setDeleteKeywordId(kw.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="bg-grey-100 px-5 py-2.5 rounded-xl border mb-6 border-grey-1000 max-w-xl dark:border-grey-600 dark:bg-blue-900">
            <span className="uppercase text-grey-600 dark:text-blue-100">No Keywords Found</span>
          </div>
        )}

        {canUpdate && (
          <Button variant="secondary" type="button" onClick={() => setCreateKeywordOpen(true)}>
            <PlusIcon className="mr-2 w-4 h-4" />
            Add keyword
          </Button>
        )}
      </div>

      {/* Create Keyword Sheet */}
      <CreateKeywordSheet
        open={createKeywordOpen}
        onOpenChange={setCreateKeywordOpen}
        existingKeywords={keywords}
        autoResponseId={autoResponse?.id}
        accountId={currentAccountId}
        didId={didId}
        onSuccess={refetch}
        onSuccessToast={showSuccessToast}
        onErrorToast={showErrorToast}
      />

      {/* Create Wildcard Sheet */}
      <CreateWildcardSheet
        open={createWildcardOpen}
        onOpenChange={setCreateWildcardOpen}
        existingKeywords={keywords}
        autoResponseId={autoResponse?.id}
        accountId={currentAccountId}
        didId={didId}
        onSuccess={refetch}
        onSuccessToast={showSuccessToast}
        onErrorToast={showErrorToast}
      />

      {/* Edit Keyword Sheet */}
      {keywordToEdit && (
        <EditKeywordSheet
          open={true}
          onOpenChange={(open) => !open && setEditKeywordId(null)}
          keywordData={keywordToEdit}
          allKeywords={keywords}
          autoResponseId={autoResponse!.id}
          canUpdate={canUpdate}
          onSuccess={refetch}
          onSuccessToast={showSuccessToast}
          onErrorToast={showErrorToast}
        />
      )}

      {/* Edit Wildcard Sheet */}
      {wildcardItem && (
        <EditWildcardSheet
          open={editWildcardOpen}
          onOpenChange={setEditWildcardOpen}
          wildcardData={wildcardItem}
          allKeywords={keywords}
          autoResponseId={autoResponse!.id}
          canUpdate={canUpdate}
          onSuccess={refetch}
          onSuccessToast={showSuccessToast}
          onErrorToast={showErrorToast}
        />
      )}

      {/* Delete Keyword Dialog */}
      <DeleteDialog
        isOpen={deleteKeywordId !== null}
        setIsOpen={(open) => !open && setDeleteKeywordId(null)}
        title="Delete Keyword Auto Responder"
        submitText={`Yes, delete keyword: ${keywordToDelete?.keyword ?? ''}`}
        cancelText="No, do not delete"
        deleteOnClick={handleDeleteKeyword}
      >
        <p className="mx-auto mb-10 max-w-sm text-xl text-center">
          Are you sure you would like to delete the following keyword auto responder?
          <br />
          KEYWORD: <strong>{keywordToDelete?.keyword ?? ''}</strong>
        </p>
      </DeleteDialog>

      {/* Delete Wildcard Dialog */}
      <DeleteDialog
        isOpen={deleteWildcardOpen}
        setIsOpen={setDeleteWildcardOpen}
        title="Delete General Auto Responder"
        submitText="Yes, delete general auto responder"
        cancelText="No, do not delete"
        deleteOnClick={handleDeleteWildcard}
      >
        <p className="mx-auto mb-10 max-w-sm text-xl text-center">
          Are you sure you would like to delete the general auto responder?
        </p>
      </DeleteDialog>

      {/* Toast notifications */}
      <Toast
        open={toast?.type === 'success'}
        title={toast?.message ?? ''}
        variant="success"
        fade="left"
        position="bottom-0 right-0"
        icon="check"
        fixed
      />
      <Toast
        open={toast?.type === 'error'}
        title={toast?.message ?? ''}
        variant="error"
        fade="left"
        position="bottom-0 right-0"
        icon="error"
        fixed
      />
    </Card>
  );
};

export default AutoResponderCrudForm;
